import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { cn } from '../../utils/cn';
import { useScrollDirectionVisibility } from '../../hooks/useScrollDirectionVisibility';
import { HotsiteBadge } from './HotsiteBadge';
import { TermsNlDigitalModalLazy } from './TermsNlDigitalModal.lazy';

const KVK_NUMBER = '86673572';
const BTW_NUMBER = 'NL864044331B01';

const FOOTER_LINK =
  'inline-flex min-h-[2rem] items-center rounded-md px-1.5 py-0.5 text-inherit transition-colors duration-200 hover:text-[#1d1d1f] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2';

async function copyToClipboard(text) {
  try {
    if (window.navigator?.clipboard?.writeText) {
      await window.navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function FooterSeparator() {
  return (
    <span
      className="mx-1.5 hidden select-none text-[#d2d2d7] sm:inline"
      aria-hidden="true"
    >
      ·
    </span>
  );
}

function FooterNavItem({ children, className }) {
  return <span className={cn('inline-flex items-center', className)}>{children}</span>;
}

FooterNavItem.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

/**
 * Site-wide footer: fixed bottom bar that slides in after scrolling down
 * and hides while scrolling up. Same width/content as home & contact.
 */
export function Footer({ className }) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [toast, setToast] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const toastTimerRef = useRef(null);
  const copiedTimerRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const isVisible = useScrollDirectionVisibility({
    forceVisible: termsModalOpen || isPinned,
  });

  const clearToastTimer = useCallback(() => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  }, []);

  const clearCopiedTimer = useCallback(() => {
    if (copiedTimerRef.current) {
      window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearToastTimer();
      clearCopiedTimer();
    },
    [clearToastTimer, clearCopiedTimer],
  );

  const showToast = useCallback(
    (kind) => {
      clearToastTimer();
      setToast(kind);
      toastTimerRef.current = window.setTimeout(() => {
        setToast(null);
        toastTimerRef.current = null;
      }, 2200);
    },
    [clearToastTimer],
  );

  const flashCopied = useCallback(
    (id) => {
      clearCopiedTimer();
      setCopiedId(id);
      copiedTimerRef.current = window.setTimeout(() => {
        setCopiedId(null);
        copiedTimerRef.current = null;
      }, 1600);
    },
    [clearCopiedTimer],
  );

  const handleCopy = useCallback(
    async (id, value) => {
      const ok = await copyToClipboard(value);
      if (ok) {
        flashCopied(id);
      }
      showToast(ok ? 'ok' : 'error');
    },
    [flashCopied, showToast],
  );

  const toastMessage = toast === 'error' ? t('footer.copyFailedToast') : toast === 'ok' ? t('footer.copiedToast') : '';

  const copyButtonClass = (id) =>
    cn(
      FOOTER_LINK,
      'border-0 bg-transparent font-inherit tabular-nums tracking-tight',
      'underline decoration-dotted decoration-[#86868b]/40 underline-offset-[3px]',
      'hover:decoration-accent hover:text-accent',
      copiedId === id && 'text-accent decoration-accent/70',
    );

  return (
    <motion.footer
      initial={false}
      animate={{ y: isVisible ? 0 : '100%' }}
      transition={
        reduceMotion
          ? { duration: 0 }
          : { duration: 0.28, ease: [0.25, 0.1, 0.25, 1] }
      }
      inert={!isVisible}
      aria-hidden={!isVisible}
      onMouseEnter={() => setIsPinned(true)}
      onMouseLeave={() => setIsPinned(false)}
      onFocusCapture={() => setIsPinned(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsPinned(false);
        }
      }}
      className={cn(
        'fixed bottom-0 left-0 right-0 z-40 border-t border-black/[0.06]',
        'bg-white/90 py-safe backdrop-blur-xl supports-[backdrop-filter]:bg-white/80',
        !isVisible && 'pointer-events-none',
        className,
      )}
    >
      <AnimatePresence>
        {toast && (
          <motion.div
            role="status"
            aria-live="polite"
            style={{ left: '50%' }}
            initial={reduceMotion ? false : { opacity: 0, y: 6, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={reduceMotion ? { opacity: 0, x: '-50%' } : { opacity: 0, y: 4, x: '-50%' }}
            transition={{ duration: reduceMotion ? 0.12 : 0.2, ease: 'easeOut' }}
            className="pointer-events-none fixed bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] z-50 px-4 sm:bottom-[calc(env(safe-area-inset-bottom)+4.75rem)]"
          >
            <span className="inline-block rounded-full border border-black/[0.06] bg-[#1d1d1f]/[0.92] px-3 py-1 text-[10px] font-medium tracking-wide text-white shadow-lg backdrop-blur-sm">
              {toastMessage}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mx-auto max-w-6xl px-safe md:px-8">
        <div className="flex flex-col items-center gap-1 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:py-2.5">
          <nav
            aria-label={t('footer.navLabel')}
            className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0 text-[10px] leading-none text-[#86868b] sm:gap-x-0 sm:text-[11px]"
          >
            <FooterNavItem>
              <button
                type="button"
                onClick={() => setTermsModalOpen(true)}
                className={FOOTER_LINK}
              >
                {t('footer.terms')}
              </button>
            </FooterNavItem>
            <FooterSeparator />
            <FooterNavItem>
              <Link to="/cookies" className={FOOTER_LINK}>
                {t('footer.cookies')}
              </Link>
            </FooterNavItem>
            <FooterSeparator />
            <FooterNavItem>
              <Link to="/imprint" className={FOOTER_LINK}>
                {t('footer.imprint')}
              </Link>
            </FooterNavItem>
          </nav>

          <p className="flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5 text-center text-[10px] leading-none text-[#86868b] sm:justify-end sm:text-[11px]">
            <span className="tabular-nums">
              <span className="text-[#aeaeb2]">{t('footer.kvk')}</span>{' '}
              <button
                type="button"
                onClick={() => handleCopy('kvk', KVK_NUMBER)}
                title={t('footer.clickToCopy')}
                aria-label={t('footer.copyKvkAria')}
                className={copyButtonClass('kvk')}
              >
                {KVK_NUMBER}
              </button>
            </span>
            <span className="mx-1 text-[#d2d2d7]" aria-hidden="true">
              ·
            </span>
            <span className="tabular-nums">
              <span className="text-[#aeaeb2]">{t('footer.btw')}</span>{' '}
              <button
                type="button"
                onClick={() => handleCopy('btw', BTW_NUMBER)}
                title={t('footer.clickToCopy')}
                aria-label={t('footer.copyBtwAria')}
                className={copyButtonClass('btw')}
              >
                {BTW_NUMBER}
              </button>
            </span>
            <span className="mx-1 text-[#d2d2d7]" aria-hidden="true">
              ·
            </span>
            <span className="whitespace-nowrap text-[#aeaeb2]">
              © {currentYear} SAMBAL
            </span>
          </p>
        </div>

        <HotsiteBadge className="border-t border-black/[0.04] pb-1.5 pt-1 sm:pb-2" />
      </div>

      {termsModalOpen && (
        <Suspense fallback={null}>
          <TermsNlDigitalModalLazy isOpen onClose={() => setTermsModalOpen(false)} />
        </Suspense>
      )}
    </motion.footer>
  );
}

Footer.propTypes = {
  className: PropTypes.string,
};
